import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Email,
  Phone,
  Edit,
  Delete,
  Visibility,
  Person,
} from '@mui/icons-material';
import { Client, ClientStatus, ClientSource } from '@/types';
import { format } from 'date-fns';

interface ClientCardProps {
  client: Client;
  onView?: (client: Client) => void;
  onEdit?: (client: Client) => void;
  onDelete?: (client: Client) => void;
  showActions?: boolean;
  compact?: boolean;
}

const ClientCard: React.FC<ClientCardProps> = ({
  client,
  onView,
  onEdit,
  onDelete,
  showActions = true,
  compact = false,
}) => {
  const getStatusColor = (status: ClientStatus) => {
    const colorMap: { [key: string]: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' } = {
      lead: 'default',
      prospect: 'primary',
      active: 'success',
      under_contract: 'warning',
      closed: 'success',
      inactive: 'error',
    };
    return colorMap[status] || 'default';
  };

  const getSourceColor = (source: ClientSource) => {
    const colorMap: { [key: string]: 'default' | 'primary' | 'secondary' } = {
      website: 'primary',
      referral: 'secondary',
      social_media: 'default',
      advertisement: 'primary',
      open_house: 'secondary',
      cold_call: 'default',
      partner: 'primary',
    };
    return colorMap[source] || 'default';
  };

  const handleView = () => {
    if (onView) {
      onView(client);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(client);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(client);
    }
  };

  const budgetRange = client.preferences.priceRange
    ? `${new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: client.preferences.priceRange.currency || 'USD',
        minimumFractionDigits: 0,
      }).format(client.preferences.priceRange.min)} - ${new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: client.preferences.priceRange.currency || 'USD',
        minimumFractionDigits: 0,
      }).format(client.preferences.priceRange.max)}`
    : 'Not specified';

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: onView ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': onView
          ? {
              transform: 'translateY(-4px)',
              boxShadow: 4,
            }
          : {},
      }}
      onClick={handleView}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            src={client.profile.avatar}
            sx={{ width: 56, height: 56, mr: 2 }}
          >
            {client.profile.firstName[0]}{client.profile.lastName[0]}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="h3" noWrap>
              {client.profile.firstName} {client.profile.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {client.profile.email}
            </Typography>
          </Box>
          <Box>
            <Tooltip title="AI Lead Score">
              <Chip
                label={`Score: ${client.matchingProfile?.priority?.quality || 75}/100`}
                size="small"
                sx={{ 
                  bgcolor: (client.matchingProfile?.priority?.quality || 75) > 80 ? 'rgba(138, 154, 91, 0.1)' : 'rgba(197, 160, 89, 0.1)',
                  color: (client.matchingProfile?.priority?.quality || 75) > 80 ? '#8A9A5B' : '#C5A059',
                  fontWeight: 700,
                  fontSize: '0.7rem'
                }}
              />
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={client.status}
            color={getStatusColor(client.status)}
            size="small"
          />
          <Chip
            label={client.source}
            color={getSourceColor(client.source)}
            size="small"
            variant="outlined"
          />
        </Box>

        {!compact && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
              <Phone fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">{client.profile.phone}</Typography>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Budget Range:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {budgetRange}
              </Typography>
            </Box>

            {client.preferences.propertyTypes && client.preferences.propertyTypes.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Interested In:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {client.preferences.propertyTypes.slice(0, 3).map((type, index) => (
                    <Chip key={index} label={type} size="small" variant="outlined" />
                  ))}
                  {client.preferences.propertyTypes.length > 3 && (
                    <Chip
                      label={`+${client.preferences.propertyTypes.length - 3}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            )}

            {client.tags && client.tags.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {client.tags.slice(0, 3).map((tag, index) => (
                    <Chip key={index} label={tag} size="small" />
                  ))}
                </Box>
              </Box>
            )}

            {client.interactions && client.interactions.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                {client.interactions.length} interaction{client.interactions.length !== 1 ? 's' : ''}
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Added: {format((client.createdAt as any).toDate(), 'MMM dd, yyyy')}
            </Typography>
          </>
        )}
      </CardContent>

      {showActions && (
        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Visibility />}
            onClick={handleView}
          >
            View
          </Button>
          <Box>
            {onEdit && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={handleEdit} color="primary">
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={handleDelete} color="error">
                  <Delete />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </CardActions>
      )}
    </Card>
  );
};

export default ClientCard;

